// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    AddBuildPlanItemRequest,
    AdvancedSearchRequest,
    AdvancedSearchResults,
    AssessmentsReopenRequest,
    Asset,
    ChangeProductSalesFacilitatorPayload,
    CnxParticipant,
    GetCompassUrlResponse,
    Participant,
    ParticipantSearchResult,
    ParticipantBuildPlanItem,
    ParticipantKeyDateResponse,
    ParticipantRaterDetail,
    ProductSaleParticipant,
    ReleaseProductSaleResult,
    SessionParticipantsAssessmentStatus,
    SetAttendanceResponse,
    SetParticipantAccessPayload,
    SetParticipantAttendancePayload,
    SetParticipantExpirationPayload,
    SetParticipantDeadlinePayload,
    SetParticipantInvitationPayload,
    SetParticipantStatusPayload,
    SetSessionAttendancePayload,
    SimpleParticipant,
    UpdateParticipantBuildPlanStatusRequest,
    ProductSaleRegistrationRequest,
    RegisterPartnerSessionParticipantsRequest,
    ProductSaleParticipantSendInvitationRequest,
    CancelProductSaleParticipantRequest,
    CancelProductSaleParticipantResponse,
    TransferParticipantRequest,
    TransferParticipantResponse,
    ProductSaleParticipantSendInvitationResponse,
    ResendParticipantInvitationsRequest,
    UpdateUserNameRequest,
    UpdateUserNameResponse,
    CurrentUserNameResponse,
} from "./types/enterpriseParticipantApiTypes";
import { IntakeSurveyResponse, ParticipantsByDateQueryArgs } from "./types/rtkQueryTypes";
import {
    CnxNotifyCoachAssignedPayload,
    CnxNotifyCoachesProposedPayload,
} from "./types/sessionDetailTypes";
import { ParticipantInstrumentStatus } from "./types/scoringStatusTypes";
import { cclTokenedAssessmentApi } from "./cclTokenedAssessmentApi";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;


// Define a service using a base URL and expected endpoints
export const cclTokenedEnterpriseParticipantApi = createApi({
    reducerPath: "cclTokenedEnterpriseParticipantApi",
    tagTypes: [
        "Participants",
        "CnxParticipants",
        "Participant",
        "ProdSalesByFacilitator",
        "SimpleParticipants",
        "CoachParticipants",
        "ParticipantKeyDates",
        "ParticipantBuildPlan",
        "ParticipantAssessmentStatus",
        "SessionParticipantsAssessmentStatus",
        "ParticipantsRaters",
        "SessionRaters",
        "Inventory",
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        setParticipantAttendance: builder.mutation<
            SetAttendanceResponse,
            SetParticipantAttendancePayload
        >({
            query: (args) => ({
                url: `enterprise-participant/EventParticipants/attendance/${args.esiKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SetParticipantAttendancePayload) => [
                { type: "Participants", id: String(args.esKey) },
            ],
        }),
        setParticipantExpirationDate: builder.mutation<boolean, SetParticipantExpirationPayload>({
            query: (args) => ({
                url: `enterprise-participant/EventParticipants/expiration-date/${args.esiKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SetParticipantExpirationPayload) => [
                { type: "Participant", id: args.esiKey },
            ],
        }),
        getParticipantKeyDates: builder.query<ParticipantKeyDateResponse, number>({
            query: (esikey) => `enterprise-participant/EventParticipants/key-dates/${esikey}`,
            providesTags: (_, __, esikey) => [{ type: "ParticipantKeyDates", id: esikey }],
        }),
        setParticipantAccessDate: builder.mutation<boolean, SetParticipantAccessPayload>({
            query: (args) => ({
                url: `enterprise-participant/EventParticipants/access-date/${args.esiKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SetParticipantAccessPayload) => [
                { type: "ParticipantKeyDates", id: args.esiKey },
                { type: "Participants", id: String(args.esKey) },
            ],
        }),
        setParticipantInvitationDate: builder.mutation<boolean, SetParticipantInvitationPayload>({
            query: (args) => ({
                url: `enterprise-participant/EventParticipants/invitation-date/${args.esiKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SetParticipantInvitationPayload) => [
                { type: "ParticipantKeyDates", id: args.esiKey },
                { type: "Participants", id: String(args.esKey) },
            ],
        }),
        setParticipantDeadlineDate: builder.mutation<boolean, SetParticipantDeadlinePayload>({
            query: (args) => ({
                url: `enterprise-participant/EventParticipants/deadline-date/${args.esiKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SetParticipantDeadlinePayload) => [
                { type: "ParticipantKeyDates", id: args.esiKey },
                { type: "Participants", id: String(args.esKey) },
            ],
        }),
        setParticipantStatus: builder.mutation<boolean, SetParticipantStatusPayload>({
            query: (args) => ({
                url: `enterprise-participant/EventParticipants/status/${args.esiKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SetParticipantStatusPayload) => [
                { type: "Participant", id: args.esiKey },
            ],
        }),
        setSessionAttendance: builder.mutation<SetAttendanceResponse, SetSessionAttendancePayload>({
            query: (args) => ({
                url: `enterprise-participant/EventParticipants/attendance/by-session/${args.esKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SetSessionAttendancePayload) => [
                { type: "Participants", id: String(args.esKey) },
            ],
        }),
        getCclParticipantsByDate: builder.mutation<
            ParticipantSearchResult[],
            Partial<ParticipantsByDateQueryArgs>
        >({
            query: (args) => ({
                url: "enterprise-participant/EventParticipants/by-session-date",
                method: "POST",
                body: args,
            }),
        }),
        notifyCnxCoachAssigned: builder.mutation<boolean, CnxNotifyCoachAssignedPayload>({
            query: (args) => ({
                url: `enterprise-coach-assignment/v1.0/cnxcoaching/${args.esikey}/assign-coach`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: CnxNotifyCoachAssignedPayload) => [
                { type: "CnxParticipants", id: args.eskey },
            ],
        }),
        notifyCnxCoachesProposed: builder.mutation<boolean, CnxNotifyCoachesProposedPayload>({
            query: (args) => ({
                url: `enterprise-coach-assignment/v1.0/cnxcoaching/${args.esikey}/proposed-coaches`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: CnxNotifyCoachesProposedPayload) => [
                { type: "CnxParticipants", id: args.eskey },
            ],
        }),
        getCclParticipantsByEventKey: builder.query<Participant[], string>({
            query: (eskey) => `enterprise-participant/EventParticipants/by-session-key/${eskey}`,
            providesTags: (_, __, eskey) => [{ type: "Participants", id: eskey }],
        }),
        getCclSimpleParticipantsByEventKey: builder.query<SimpleParticipant[], string>({
            query: (eskey) =>
                `enterprise-participant/EventParticipants/by-session-key/limited/${eskey}`,
            providesTags: (_, __, eskey) => [{ type: "SimpleParticipants", id: eskey }],
        }),
        getCnxParticipantsByEventKey: builder.query<CnxParticipant[], string>({
            query: (eskey) =>
                `enterprise-participant/EventParticipants/by-cnx-session-key/${eskey}`,
            providesTags: (_, __, esKey) => [{ type: "CnxParticipants", id: esKey }],
        }),
        getCclParticipantsByEmail: builder.query<ParticipantSearchResult[], string>({
            query: (email) =>
                `enterprise-participant/EventParticipants/by-email/${encodeURIComponent(email)}`,
        }),
        getCclParticipantsByCoachImKey: builder.query<Participant[], number>({
            query: (imkey) => `enterprise-participant/EventParticipants/by-coach/${imkey}`,
            providesTags: (_, __, imkey) => [{ type: "CoachParticipants", id: imkey }],
        }),
        getCclParticipantsNonarchivedByCoachImKey: builder.query<Participant[], number>({
            query: (imkey) =>
                `enterprise-participant/EventParticipants/by-coach/${imkey}/nonarchived`,
            providesTags: (_, __, imkey) => [{ type: "CoachParticipants", id: imkey }],
        }),
        getCclParticipantsArchivedByCoachImKey: builder.query<Participant[], number>({
            query: (imkey) => `enterprise-participant/EventParticipants/by-coach/${imkey}/archived`,
            providesTags: (_, __, imkey) => [{ type: "CoachParticipants", id: imkey }],
        }),
        getCclParticipantsWithFilesByCoachImKey: builder.query<Participant[], number>({
            query: (imkey) =>
                `enterprise-participant/EventParticipants/by-coach-with-files/${imkey}`,
            providesTags: (_, __, imkey) => [{ type: "CoachParticipants", id: imkey }],
        }),
        getCclParticipantsNonarchivedWithFilesByCoachImKey: builder.query<Participant[], number>({
            query: (imkey) =>
                `enterprise-participant/EventParticipants/by-coach-with-files/${imkey}/nonarchived`,
            providesTags: (_, __, imkey) => [{ type: "CoachParticipants", id: imkey }],
        }),
        getCclParticipantsArchivedWithFilesByCoachImKey: builder.query<Participant[], number>({
            query: (imkey) =>
                `enterprise-participant/EventParticipants/by-coach-with-files/${imkey}/archived`,
            providesTags: (_, __, imkey) => [{ type: "CoachParticipants", id: imkey }],
        }),
        archiveParticipant: builder.mutation<
            Participant[],
            { imkey: number; esiKeys: number[]; onBehalfOfImKey?: number }
        >({
            query: (args) => ({
                url: "enterprise-participant/EventParticipants/archive",
                method: "POST",
                body: { ids: args.esiKeys, onBehalfOfImKey: args.onBehalfOfImKey },
            }),
            invalidatesTags: (_, __, args) => [{ type: "CoachParticipants", id: args.imkey }],
        }),
        unarchiveParticipant: builder.mutation<
            Participant[],
            { imkey: number; esiKeys: number[]; onBehalfOfImKey?: number }
        >(
            {
                query: (args) => ({
                    url: "enterprise-participant/EventParticipants/unarchive",
                    method: "POST",
                    body: { ids: args.esiKeys, onBehalfOfImKey: args.onBehalfOfImKey },
                }),
                invalidatesTags: (_, __, args) => [{ type: "CoachParticipants", id: args.imkey }],
            },
        ),
        getCclParticipant: builder.query<Participant, string>({
            query: (esikey) => `enterprise-participant/EventParticipants/${esikey}`,
            providesTags: (_, __, esikey) => [{ type: "Participant", id: esikey }],
        }),
        getCclParticipantSessions: builder.query<number[], string>({
            query: (esikey) => `enterprise-participant/EventParticipants/${esikey}/sessions`,
        }),
        getCclParticipantSessionFiles: builder.query<Asset[], string>({
            query: (esikey) =>
                `enterprise-participant/EventParticipantFiles/by-participant-key/${esikey}`,
        }),
        getMultipleCclParticipantSessionFiles: builder.mutation<Asset[], number[]>({
            query: (args) => ({
                url: "enterprise-participant/EventParticipantFiles/by-participant-keys",
                method: "POST",
                body: args, // esikeys
            }),
        }),
        getCclCnxParticipantIntakeSurveyUrl: builder.query<IntakeSurveyResponse, string>({
            query: (esikey) => `enterprise-coach-assignment/v1.0/cnxcoaching/${esikey}/survey-url`,
        }),
        changeProductSalesFacilitator: builder.mutation<
            boolean,
            ChangeProductSalesFacilitatorPayload
        >({
            query: (args) => ({
                url: `enterprise-participant/ProductSaleParticipants/change-facilitator/${encodeURIComponent(
                    args.facilitatorEmail,
                )}`,
                method: "POST",
                body: args.reportIds,
            }),
        }),
        getProductSaleParticipantsByDate: builder.mutation<
            ProductSaleParticipant[],
            Partial<ParticipantsByDateQueryArgs>
        >({
            query: (args) => ({
                url: "enterprise-participant/ProductSaleParticipants/by-score-date",
                method: "POST",
                body: args,
            }),
        }),
        getProductSaleParticipantsByParticipantEmail: builder.query<
            ProductSaleParticipant[],
            string
        >({
            query: (email) =>
                `enterprise-participant/ProductSaleParticipants/by-participant-email/${encodeURIComponent(
                    email,
                )}`,
        }),
        getProductSaleParticipantsByFacilitatorEmail: builder.query<
            ProductSaleParticipant[],
            string
        >({
            query: (email) =>
                `enterprise-participant/ProductSaleParticipants/by-facilitator-email/${encodeURIComponent(
                    email,
                )}`,
            providesTags: ["ProdSalesByFacilitator"],
        }),
        getArchivedProductSaleParticipantsByFacilitatorEmail: builder.query<
            ProductSaleParticipant[],
            string
        >({
            query: (email) =>
                `enterprise-participant/ProductSaleParticipants/by-facilitator-email/${encodeURIComponent(
                    email,
                )}/archived`,
            providesTags: ["ProdSalesByFacilitator"],
        }),
        getUnarchivedProductSaleParticipantsByFacilitatorEmail: builder.query<
            ProductSaleParticipant[],
            string
        >({
            query: (email) =>
                `enterprise-participant/ProductSaleParticipants/by-facilitator-email/${encodeURIComponent(
                    email,
                )}/nonarchived`,
            providesTags: ["ProdSalesByFacilitator"],
        }),
        getProductSaleParticipantsByFacilitatorOrParticipantEmail: builder.query<
            ProductSaleParticipant[],
            string
        >({
            async queryFn(email, _queryApi, _extraOptions, fetchWithBQ) {
                const resultByPaxEmail = await fetchWithBQ(
                    `enterprise-participant/ProductSaleParticipants/by-participant-email/${encodeURIComponent(
                        email,
                    )}`,
                );
                if (resultByPaxEmail.error)
                    return { error: resultByPaxEmail.error as FetchBaseQueryError };

                const resultByFacEmail = await fetchWithBQ(
                    `enterprise-participant/ProductSaleParticipants/by-facilitator-email/${encodeURIComponent(
                        email,
                    )}`,
                );
                if (resultByFacEmail.error)
                    return { error: resultByFacEmail.error as FetchBaseQueryError };
                return {
                    data: [
                        ...(resultByPaxEmail.data as ProductSaleParticipant[]),
                        ...(resultByFacEmail.data as ProductSaleParticipant[]),
                    ],
                };
            },
        }),
        releaseProductSales: builder.mutation<ReleaseProductSaleResult[], string[]>({
            query: (args) => ({
                url: "enterprise-participant/ProductSaleParticipants/release-product-sales",
                method: "POST",
                body: args,
            }),
            invalidatesTags: ["ProdSalesByFacilitator"],
        }),
        archiveProductSaleParticipants: builder.mutation<
            ReleaseProductSaleResult[],
            { reportIds: string[]; onBehalfOfImKey?: number }
        >({
            query: (args) => ({
                url: "enterprise-participant/ProductSaleParticipants/archive",
                method: "POST",
                body: { ids: args.reportIds, onBehalfOfImKey: args.onBehalfOfImKey },
            }),
            invalidatesTags: ["ProdSalesByFacilitator"],
        }),
        unarchiveProductSaleParticipants: builder.mutation<
            ProductSaleParticipant[],
            { reportIds: string[]; onBehalfOfImKey?: number }
        >({
            query: (args) => ({
                url: "enterprise-participant/ProductSaleParticipants/unarchive",
                method: "POST",
                body: { ids: args.reportIds, onBehalfOfImKey: args.onBehalfOfImKey },
            }),
            invalidatesTags: ["ProdSalesByFacilitator"],
        }),
        getParticipantCompassUrl: builder.query<GetCompassUrlResponse, number>({
            query: (imkey) => `enterprise-participant/EventParticipants/get-compass-url/${imkey}`,
        }),
        resendRaterInvitations: builder.mutation<boolean, number[]>({
            query: (esikeys) => ({
                url: "enterprise-participant/Raters/resend-rater-invitations",
                method: "POST",
                body: esikeys,
            }),
        }),
        sendRaterReminders: builder.mutation<boolean, number[]>({
            query: (esikeys) => ({
                url: "enterprise-participant/Raters/send-rater-reminders",
                method: "POST",
                body: esikeys,
            }),
        }),
        getParticipantBuildPlan: builder.query<ParticipantBuildPlanItem[], number>({
            query: (esikey) => `enterprise-participant/Assessments/${esikey}/build-plan`,
            providesTags: (_, __, esikey) => [{ type: "ParticipantBuildPlan", id: esikey }],
        }),
        getParticipantAdditionalAssessments: builder.query<ParticipantInstrumentStatus[], number>({
            query: (esikey) =>
                `enterprise-participant/Assessments/additional-assessments/${esikey}`,
        }),
        enableParticipantBuildPlanItem: builder.mutation<
            void,
            UpdateParticipantBuildPlanStatusRequest
        >({
            query: (args) => ({
                url: "enterprise-participant/Assessments/enable-build-plan-item",
                method: "POST",
                body: args,
            }),
            invalidatesTags: (_, __, args: UpdateParticipantBuildPlanStatusRequest) => [
                { type: "ParticipantBuildPlan", id: args.esiKey },
            ],
        }),
        disableParticipantBuildPlanItem: builder.mutation<
            void,
            UpdateParticipantBuildPlanStatusRequest
        >({
            query: (args) => ({
                url: "enterprise-participant/Assessments/disable-build-plan-item",
                method: "POST",
                body: args,
            }),
            invalidatesTags: (_, __, args: UpdateParticipantBuildPlanStatusRequest) => [
                { type: "ParticipantBuildPlan", id: args.esiKey },
            ],
        }),
        addBuildPlanItem: builder.mutation<void, AddBuildPlanItemRequest>({
            query: (args) => ({
                url: "enterprise-participant/Assessments/add-build-plan-item",
                method: "POST",
                body: args,
            }),
            invalidatesTags: (_, __, args: AddBuildPlanItemRequest) => [
                { type: "ParticipantBuildPlan", id: args.esiKey },
            ],
        }),
        sendParticipantReminders: builder.mutation<boolean, number[]>({
            query: (args) => ({
                url: "enterprise-participant/Assessments/send-reminders",
                method: "POST",
                body: args,
            }),
        }),
        resendParticipantInvitations: builder.mutation<
            boolean,
            ResendParticipantInvitationsRequest
        >({
            query: (request) => ({
                url: "enterprise-participant/Assessments/resend-invitations",
                method: "POST",
                body: request.participantEsiKeys,
            }),
            invalidatesTags: (_, __, request) => [
                { type: "Participants", id: String(request.esKey) },
            ],
            async onQueryStarted(request, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(
                    cclTokenedAssessmentApi.util.invalidateTags([
                        { type: "Inventory", id: request.esKey },
                    ]),
                );
            },
        }),
        getParticipantAssessmentStatus: builder.query<SessionParticipantsAssessmentStatus, number>({
            query: (esikey) => `enterprise-participant/Assessments/status/${esikey}`,
            providesTags: (_, __, esikey) => [{ type: "ParticipantAssessmentStatus", id: esikey }],
        }),
        getSessionParticipantsAssessmentStatus: builder.query<
            SessionParticipantsAssessmentStatus,
            number
        >({
            query: (eskey) => `enterprise-participant/Assessments/session-status/${eskey}`,
            providesTags: (_, __, eskey) => [
                { type: "SessionParticipantsAssessmentStatus", id: eskey },
            ],
        }),
        getProgramManagerParticipants: builder.query<ParticipantSearchResult[], number>({
            query: (imkey) =>
                `enterprise-participant/EventParticipants/by-program-manager/${imkey}`,
            providesTags: (_, __, imkey) => [{ type: "CoachParticipants", id: imkey }],
        }),
        getParticipantsRaters: builder.query<ParticipantRaterDetail[], number>({
            query: (esikey) => `enterprise-participant/Assessments/raters/${esikey}`,
            providesTags: (_, __, esikey) => [{ type: "ParticipantsRaters", id: esikey }],
        }),
        getSessionRaters: builder.query<ParticipantRaterDetail[], number>({
            query: (eskey) => `enterprise-participant/Assessments/session-raters/${eskey}`,
            providesTags: (_, __, eskey) => [{ type: "SessionRaters", id: eskey }],
        }),
        reopenAssessments: builder.mutation<void, AssessmentsReopenRequest>({
            query: (args) => ({
                url: `enterprise-participant/Assessments/reopen-assessments`,
                method: "POST",
                body: args,
            }),
            invalidatesTags: (_, __, args: AssessmentsReopenRequest) =>
                args.requestItems.map((item) => ({
                    type: "ParticipantBuildPlan",
                    id: item.esiKey,
                })),
        }),
        getAdvancedSearchOptions: builder.query<any, void>({
            query: () => `enterprise-participant/AdvancedSearch/advanced-search-options`,
        }),
        AdvancedRegistrationSearch: builder.mutation<AdvancedSearchResults, AdvancedSearchRequest>({
            query: (args) => ({
                url: `enterprise-participant/AdvancedSearch/advanced-search`,
                method: "POST",
                body: args,
            }),
        }),
        registerProductSaleParticipant: builder.mutation<
            SimpleParticipant[],
            ProductSaleRegistrationRequest
        >({
            query: (request) => ({
                url: "enterprise-participant/ProductSaleParticipants/register",
                method: "POST",
                body: request,
            }),
            invalidatesTags: (_, __, request) => [
                { type: "Participants", id: String(request.esKey) },
                { type: "CoachParticipants" },
            ],
            async onQueryStarted(request, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(
                    cclTokenedAssessmentApi.util.invalidateTags([
                        { type: "Inventory", id: request.esKey },
                    ]),
                );
            },
        }),
        sendProductSaleParticipantsInvitation: builder.mutation<
            ProductSaleParticipantSendInvitationResponse,
            ProductSaleParticipantSendInvitationRequest
        >({
            query: (payload) => ({
                url: "enterprise-participant/ProductSaleParticipants/send-invitations",
                method: "POST",
                body: payload.esiKeys,
            }),
            invalidatesTags: (_, __, request) => [
                { type: "Participants", id: String(request.esKey) },
            ],
            async onQueryStarted(request, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(
                    cclTokenedAssessmentApi.util.invalidateTags([
                        { type: "Inventory", id: request.esKey },
                    ]),
                );
            },
        }),
        transferProductSaleParticipants: builder.mutation<
            TransferParticipantResponse,
            TransferParticipantRequest
        >({
            query: (payload) => ({
                url: "enterprise-participant/ProductSaleParticipants/transfer-registrations",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: (_, __, request) => [
                { type: "Participants", id: String(request.sourceEsKey) },
            ],
        }),
        transferPartnerSessionParticipants: builder.mutation<
            TransferParticipantResponse,
            TransferParticipantRequest
        >({
            query: (payload) => ({
                url: "enterprise-participant/EventParticipants/transfer-partner-session",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: (_, __, request) => [
                { type: "Participants", id: String(request.sourceEsKey) },
            ],
        }),
        cancelProductSaleParticipants: builder.mutation<
            CancelProductSaleParticipantResponse,
            CancelProductSaleParticipantRequest
        >({
            query: (payload) => ({
                url: "enterprise-participant/ProductSaleParticipants/cancel-registrations",
                method: "POST",
                body: payload.esiKeys,
            }),
            invalidatesTags: (_, __, payload) => [
                { type: "Participants", id: String(payload.esKey) },
            ],
        }),
        registerPartnerSessionParticipant: builder.mutation<
            SimpleParticipant[],
            RegisterPartnerSessionParticipantsRequest
        >({
            query: (request) => ({
                url: "enterprise-participant/EventParticipants/register-partner-session",
                method: "POST",
                body: request,
            }),
            invalidatesTags: (_, __, request) => [
                { type: "Participants", id: String(request.esKey) },
                { type: "CoachParticipants" },
            ],
        }),
        updateUserName: builder.mutation<
            UpdateUserNameResponse,
            UpdateUserNameRequest & { esiKey: number }
        >({
            query: ({ esiKey, ...apiPayload }) => ({
                url: "enterprise-participant/EventParticipants/update-user-name",
                method: "PUT",
                body: apiPayload,
            }),
            invalidatesTags: (_, __, { esiKey }) => [{ type: "Participant", id: esiKey }],
        }),
        getCurrentUserName: builder.query<CurrentUserNameResponse, void>({
            query: () => "enterprise-participant/EventParticipants/current-user-name",
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useSetParticipantAttendanceMutation,
    useSetSessionAttendanceMutation,
    useSetParticipantExpirationDateMutation,
    useSetParticipantStatusMutation,
    useGetCclParticipantsByDateMutation,
    useGetCclParticipantsByEventKeyQuery,
    useLazyGetCclParticipantsByEventKeyQuery,
    useGetCnxParticipantsByEventKeyQuery,
    useGetCclParticipantsByEmailQuery,
    useLazyGetCclParticipantsByEmailQuery,
    useGetCclParticipantsByCoachImKeyQuery,
    useGetCclParticipantsArchivedByCoachImKeyQuery,
    useLazyGetCclParticipantsWithFilesByCoachImKeyQuery,
    useGetCclParticipantQuery,
    useGetCclParticipantSessionsQuery,
    useChangeProductSalesFacilitatorMutation,
    useGetProductSaleParticipantsByDateMutation,
    useGetProductSaleParticipantsByFacilitatorEmailQuery,
    useGetArchivedProductSaleParticipantsByFacilitatorEmailQuery,
    useLazyGetProductSaleParticipantsByFacilitatorOrParticipantEmailQuery,
    useReleaseProductSalesMutation,
    useLazyGetParticipantCompassUrlQuery,
    useArchiveParticipantMutation,
    useUnarchiveParticipantMutation,
    useArchiveProductSaleParticipantsMutation,
    useUnarchiveProductSaleParticipantsMutation,
    useResendRaterInvitationsMutation,
    useSendRaterRemindersMutation,
    useGetParticipantKeyDatesQuery,
    useSetParticipantAccessDateMutation,
    useSetParticipantInvitationDateMutation,
    useSetParticipantDeadlineDateMutation,
    useGetParticipantBuildPlanQuery,
    useEnableParticipantBuildPlanItemMutation,
    useDisableParticipantBuildPlanItemMutation,
    useAddBuildPlanItemMutation,
    useGetParticipantAdditionalAssessmentsQuery,
    useSendParticipantRemindersMutation,
    useResendParticipantInvitationsMutation,
    useGetParticipantCompassUrlQuery,
    useGetProgramManagerParticipantsQuery,
    useGetParticipantAssessmentStatusQuery,
    useGetSessionParticipantsAssessmentStatusQuery,
    useLazyGetProgramManagerParticipantsQuery,
    useLazyGetParticipantsRatersQuery,
    useLazyGetSessionRatersQuery,
    useReopenAssessmentsMutation,
    useGetAdvancedSearchOptionsQuery,
    useAdvancedRegistrationSearchMutation,
    useRegisterProductSaleParticipantMutation,
    useRegisterPartnerSessionParticipantMutation,
    useSendProductSaleParticipantsInvitationMutation,
    useTransferProductSaleParticipantsMutation,
    useTransferPartnerSessionParticipantsMutation,
    useCancelProductSaleParticipantsMutation,
    useUpdateUserNameMutation,
    useLazyGetCurrentUserNameQuery,
} = cclTokenedEnterpriseParticipantApi;
